import {
  WorkflowCategoryData,
  StepStatus,
  StepType,
  SubmitStepPayload,
  OfficeType,
  SubmitFeedbackPayload,
  KnowledgeFieldSchema,
  FeedbackEntry,
  AppOption,
  AppInputSchema,
  DeleteStepPayload,
  WorkflowProcessData,
} from "../../types/process";
import { v4 as uuidv4 } from "uuid";
import api from "../../utils/api";
import { getLoggedinUser } from "../../api/apiCore";
import { logger } from "../../utils/logger";
import { URLS } from "../../constants";

// Helper function to get company ID from logged in user
const getCompanyId = (): string | null => {
  const user = getLoggedinUser();
  return user?.companyID || null;
};

// Helper function to get base URL
const getBaseUrl = (): string => {
  return URLS.AGENT_PROXY_URL || "https://dev-agent-proxy.jesss.ai";
};

// Helper function to build common headers
export const buildApiHeaders = (
  additionalHeaders: Record<string, string> = {},
): Record<string, string> => {
  const companyId = getCompanyId();

  return {
    ...additionalHeaders,
    ...(companyId && { "X-Company-ID": companyId }),
  };
};

// Helper function to build axios config
export const buildAxiosConfig = (
  additionalHeaders: Record<string, string> = {},
): { headers: Record<string, string>; withCredentials: boolean } => {
  return {
    headers: buildApiHeaders(additionalHeaders),
    withCredentials: true,
  };
};

const serviceKnowledgeSchema: KnowledgeFieldSchema[] = [
  {
    name: "serviceName",
    label: "Service Name",
    type: "text",
    placeholder: "e.g., Basic Consultation",
    required: true,
  },
  {
    name: "price",
    label: "Price ($)",
    type: "number",
    placeholder: "e.g., 100",
    required: true,
  },
  {
    name: "duration",
    label: "Duration (min)",
    type: "number",
    placeholder: "e.g., 60",
  },
  {
    name: "description",
    label: "Service Description",
    type: "textarea",
    placeholder: "Details about the service",
  },
  { name: "isActive", label: "Is Active?", type: "boolean" },
  {
    name: "constraints",
    label: "Constraints",
    type: "text",
    placeholder: "e.g., Weekdays only",
  },
  {
    name: "serviceAgreement",
    label: "Service Agreement (PDF)",
    type: "file",
    required: true,
    fileConfig: {
      maxFiles: 1,
      maxSizeMB: 5,
      allowedTypes: ["application/pdf"],
    },
  },
];

const initialFeedback: FeedbackEntry[] = [
  {
    id: uuidv4(),
    text: "This step was a bit confusing initially.",
    date: new Date(Date.now() - 86400000 * 2).toISOString(),
    author: "User A",
  },
  {
    id: uuidv4(),
    text: "Clearer instructions would be helpful. Attached a screenshot.",
    date: new Date(Date.now() - 86400000).toISOString(),
    author: "User B",
    attachments: [
      {
        id: uuidv4(),
        name: "screenshot1.png",
        type: "image/png",
        size: 102400,
        url: "mock-url/screenshot1.png",
      },
    ],
  },
];

const slackAppInputs: AppInputSchema[] = [
  {
    name: "token",
    label: "Bot Token",
    description: "Your Slack Bot User OAuth Token (starts with xoxb-).",
    type: "password",
    required: true,
    placeholder: "xoxb-...",
  },
  {
    name: "channel",
    label: "Default Channel",
    description:
      "The default Slack channel ID to post messages to (e.g., C012345XYZ).",
    type: "text",
    required: true,
    placeholder: "C012345XYZ",
  },
];

const mailerLiteInputs: AppInputSchema[] = [
  {
    name: "apiKey",
    label: "API Key",
    description: "Your MailerLite API v2 Key.",
    type: "password",
    required: true,
  },
  {
    name: "listId",
    label: "Default List ID",
    description: "The ID of the default mailing list.",
    type: "text",
    required: true,
    placeholder: "e.g., 12345",
  },
  {
    name: "fromEmail",
    label: "From Email",
    description: "Default sender email address.",
    type: "text",
    required: true,
    placeholder: "e.g., noreply@company.com",
  },
];

const availableAppsList: AppOption[] = [
  {
    id: "crm-pro",
    name: "CRM Pro",
    isInstalled: true,
    appCategories: ["CRM", "Sales", "Customer Support"],
  },
  {
    id: "salesforce",
    name: "Salesforce",
    isInstalled: true,
    appCategories: ["CRM", "Sales"],
  },
  {
    id: "hubspot",
    name: "HubSpot",
    isInstalled: true,
    appCategories: ["CRM", "Marketing", "Sales"],
  },
  {
    id: "jira",
    name: "Jira",
    isInstalled: true,
    appCategories: ["Project Management", "Development"],
  },
  {
    id: "asana",
    name: "Asana",
    isInstalled: true,
    appCategories: ["Project Management", "Collaboration"],
  },
  {
    id: "expensify",
    name: "Expensify",
    isInstalled: true,
    appCategories: ["Finance", "Accounting"],
  },
  {
    id: "quickbooks",
    name: "Quickbooks",
    isInstalled: true,
    appCategories: ["Finance", "Accounting"],
  },
  {
    id: "slack",
    name: "Slack Messenger",
    requiredInputs: slackAppInputs,
    isInstalled: false,
    appCategories: ["Communication", "Collaboration"],
  },
  {
    id: "mailerlite",
    name: "MailerLite",
    requiredInputs: mailerLiteInputs,
    isInstalled: true,
    installationParams: {
      apiKey: "ml_secret_key_12345",
      listId: "67890",
      fromEmail: "welcome@company.com",
    },
    appCategories: ["Marketing", "Communication"],
  },
];

let mockCategories: WorkflowCategoryData[] = [
  {
    id: "cat1",
    name: "Customer Onboarding",
    type: OfficeType.FRONT_OFFICE,
    description:
      "Processes for welcoming new customers and setting up their accounts.",
    processes: [
      {
        id: "proc1-1",
        name: "New Client Registration",
        relevantAppCategory: "Communication",
        steps: [
          {
            id: "step1-1-1",
            name: "Welcome Email Sent",
            status: StepStatus.COMPLETE,
            type: StepType.APP,
            details: "Automated email via MailerLite (configured example)",
            config: { availableApps: [] },
            currentConfig: {
              selectedAppId: "mailerlite",
              appInstallationParams: {
                apiKey: "ml_secret_key_12345",
                listId: "67890",
                fromEmail: "welcome@company.com",
              },
            },
            feedbackHistory: [initialFeedback[0]],
            hasFeedback: true,
          },
          {
            id: "step1-1-2",
            name: "Collect Initial Requirements",
            status: StepStatus.INCOMPLETE,
            type: StepType.KNOWLEDGE,
            requiresConfiguration: true,
            config: {
              isStructuredKnowledge: true,
              knowledgeSchema: [
                {
                  name: "clientName",
                  label: "Client Name",
                  type: "text",
                  required: true,
                },
                {
                  name: "projectScope",
                  label: "Project Scope",
                  type: "textarea",
                  required: true,
                },
                { name: "budget", label: "Budget", type: "number" },
                {
                  name: "isRetainer",
                  label: "Retainer Client?",
                  type: "boolean",
                },
                {
                  name: "nda",
                  label: "Signed NDA (PDF)",
                  type: "file",
                  fileConfig: {
                    maxFiles: 1,
                    allowedTypes: ["application/pdf"],
                  },
                  required: true,
                },
              ],
              maxKnowledgeItems: 1,
            },
            currentConfig: { knowledgeData: [{ id: uuidv4() }] },
          },
          {
            id: "step1-1-3",
            name: "Assign Account Manager & Setup Internal Comms",
            status: StepStatus.COMPLETE,
            type: StepType.APP,
            details:
              "Account manager assigned. Internal team channel setup in Asana.",
            config: { availableApps: [] },
            currentConfig: { selectedAppId: "asana" },
            feedbackHistory: [initialFeedback[1]],
            hasFeedback: true,
          },
          {
            id: "step1-1-5",
            name: "Setup Client Communication Channel",
            status: StepStatus.INCOMPLETE,
            type: StepType.APP,
            requiresConfiguration: true,
            config: { availableApps: [] },
            currentConfig: {},
          },
          {
            id: "step1-1-4",
            name: "Initial Setup Complete",
            status: StepStatus.COMPLETE,
            type: StepType.KNOWLEDGE,
            details: "Client profile and project space created.",
            config: {
              isStructuredKnowledge: true,
              knowledgeSchema: [
                { name: "profileId", label: "Profile ID", type: "text" },
                { name: "setupDate", label: "Setup Date", type: "text" },
                { name: "notes", label: "Setup Notes", type: "textarea" },
                {
                  name: "clientLogo",
                  label: "Client Logo",
                  type: "file",
                  fileConfig: {
                    maxFiles: 1,
                    allowedTypes: ["image/png", "image/jpeg"],
                  },
                },
              ],
            },
            currentConfig: {
              type: StepType.KNOWLEDGE,
              knowledgeData: [
                {
                  id: uuidv4(),
                  profileId: "CUST001",
                  setupDate: new Date().toLocaleDateString(),
                  notes: "All initial documents uploaded by client.",
                  clientLogo: [
                    {
                      id: uuidv4(),
                      name: "logo.png",
                      type: "image/png",
                      size: 50000,
                      url: "mock-url/logo.png",
                    },
                  ],
                },
              ],
            },
          },
        ],
      },
    ],
    feedbackHistory: [initialFeedback[0]],
    hasFeedback: true,
  },
  {
    id: "cat2",
    name: "Service Delivery",
    type: OfficeType.FRONT_OFFICE,
    description: "Workflows related to providing services to customers.",
    processes: [
      {
        id: "proc2-1",
        name: "Standard Service Package",
        relevantAppCategory: "Project Management",
        steps: [
          {
            id: "step2-1-1",
            name: "Service Confirmation",
            status: StepStatus.COMPLETE,
            type: StepType.KNOWLEDGE,
            details: "Standard service terms agreed.",
            config: { isStructuredKnowledge: false },
            currentConfig: {
              type: StepType.KNOWLEDGE,
              knowledgeData:
                "Client agreed to standard terms via email on 2023-03-15.",
            },
          },
          {
            id: "step2-1-2",
            name: "Configure Service Tools",
            status: StepStatus.INCOMPLETE,
            type: StepType.HYBRID,
            requiresConfiguration: true,
            config: {
              availableApps: [],
              knowledgeSchema: serviceKnowledgeSchema,
              isStructuredKnowledge: true,
              maxKnowledgeItems: 5,
            },
            currentConfig: { knowledgeData: [] },
          },
          {
            id: "step2-1-3",
            name: "Deliver Service",
            status: StepStatus.COMPLETE,
            type: StepType.APP,
            details: "Tracked in Asana",
            config: { availableApps: [] },
            currentConfig: { selectedAppId: "asana" },
          },
          {
            id: "step2-1-4",
            name: "Client Feedback Collection",
            status: StepStatus.COMPLETE,
            type: StepType.KNOWLEDGE,
            details: "Post-service feedback survey results.",
            config: {
              isStructuredKnowledge: true,
              knowledgeSchema: [
                {
                  name: "rating",
                  label: "Overall Rating (1-5)",
                  type: "number",
                  required: true,
                },
                {
                  name: "comments",
                  label: "Client Comments",
                  type: "textarea",
                },
                {
                  name: "followUp",
                  label: "Follow-up Required?",
                  type: "boolean",
                },
                {
                  name: "surveyScan",
                  label: "Scanned Survey (Optional)",
                  type: "file",
                  fileConfig: { maxFiles: 2 },
                },
              ],
            },
            currentConfig: {
              type: StepType.KNOWLEDGE,
              knowledgeData: [
                {
                  id: uuidv4(),
                  rating: 5,
                  comments: "Excellent service, very satisfied!",
                  followUp: false,
                },
                {
                  id: uuidv4(),
                  rating: 4,
                  comments: "Good, but response time could be faster.",
                  followUp: true,
                  surveyScan: [
                    {
                      id: uuidv4(),
                      name: "survey_scan.pdf",
                      type: "application/pdf",
                      size: 120000,
                      url: "mock-url/survey.pdf",
                    },
                  ],
                },
              ],
            },
            feedbackHistory: initialFeedback,
            hasFeedback: true,
          },
        ],
      },
    ],
  },
  {
    id: "cat3",
    name: "Internal Financial Audit",
    type: OfficeType.BACK_OFFICE,
    description: "Internal processes for financial review and auditing.",
    processes: [
      {
        id: "proc3-1",
        name: "Quarterly Expense Review",
        relevantAppCategory: "Finance",
        steps: [
          {
            id: "step3-1-1",
            name: "Gather Expense Reports",
            status: StepStatus.COMPLETE,
            type: StepType.APP,
            details: "Pulled from Expensify",
            config: { availableApps: [] },
            currentConfig: { selectedAppId: "expensify" },
          },
          {
            id: "step3-1-2",
            name: "Verify Large Transactions",
            status: StepStatus.INCOMPLETE,
            type: StepType.KNOWLEDGE,
            requiresConfiguration: true,
            config: {
              isStructuredKnowledge: false,
            },
            currentConfig: { knowledgeData: "" },
          },
          {
            id: "step3-1-3",
            name: "Generate Audit Summary",
            status: StepStatus.COMPLETE,
            type: StepType.APP,
            details: "Generated via Quickbooks",
            config: { availableApps: [] },
            currentConfig: { selectedAppId: "quickbooks" },
          },
          {
            id: "step3-1-4",
            name: "Management Review Sign-off",
            status: StepStatus.COMPLETE,
            type: StepType.KNOWLEDGE,
            details: "Audit reviewed and signed off by management.",
            config: {
              isStructuredKnowledge: true,
              knowledgeSchema: [
                {
                  name: "reviewerName",
                  label: "Reviewer Name",
                  type: "text",
                  required: true,
                },
                {
                  name: "signOffDate",
                  label: "Sign-off Date",
                  type: "text",
                  required: true,
                },
                {
                  name: "approvalNotes",
                  label: "Approval Notes",
                  type: "textarea",
                },
                {
                  name: "signedDocument",
                  label: "Signed Document",
                  type: "file",
                  fileConfig: { maxFiles: 1 },
                  required: true,
                },
              ],
            },
            currentConfig: {
              type: StepType.KNOWLEDGE,
              knowledgeData: [
                {
                  id: uuidv4(),
                  reviewerName: "Jane Doe (CFO)",
                  signOffDate: new Date().toLocaleDateString(),
                  approvalNotes: "All clear, proceeding with Q3 planning.",
                  signedDocument: [
                    {
                      id: uuidv4(),
                      name: "signed_audit.pdf",
                      type: "application/pdf",
                      size: 200000,
                      url: "mock-url/signed.pdf",
                    },
                  ],
                },
              ],
            },
          },
        ],
      },
    ],
  },
];

mockCategories = mockCategories.map((category) =>
  JSON.parse(JSON.stringify(category)),
);

const getRefreshedCategories = (): WorkflowCategoryData[] => {
  return JSON.parse(
    JSON.stringify(
      mockCategories.map((cat) => ({
        ...cat,
        hasFeedback: !!(cat.feedbackHistory && cat.feedbackHistory.length > 0),
        processes: cat.processes.map((proc: WorkflowProcessData) => ({
          ...proc,
          steps: proc.steps.map((s) => {
            let filteredApps: AppOption[] = [];
            if (s.config && s.type !== StepType.KNOWLEDGE) {
              const processAppCategory = proc.relevantAppCategory;
              if (processAppCategory) {
                filteredApps = availableAppsList
                  .filter((app) =>
                    app.appCategories?.includes(processAppCategory),
                  )
                  .map((app) => {
                    const globalAppMatch = availableAppsList.find(
                      (globalApp) => globalApp.id === app.id,
                    );
                    return globalAppMatch ? { ...globalAppMatch } : { ...app };
                  });
              } else {
                filteredApps = availableAppsList.map((app) => ({ ...app }));
              }
            }

            // If step has a configured app, enrich the current config with app details
            const enrichedCurrentConfig = s.currentConfig
              ? { ...s.currentConfig }
              : undefined;
            if (enrichedCurrentConfig?.selectedAppId) {
              const selectedApp = availableAppsList.find(
                (app) => app.id === enrichedCurrentConfig.selectedAppId,
              );
              if (selectedApp) {
                // Add visible parameters (non-secret) to current config for display
                if (
                  enrichedCurrentConfig.appInstallationParams &&
                  selectedApp.requiredInputs
                ) {
                  enrichedCurrentConfig.visibleParams = {};
                  Object.entries(
                    enrichedCurrentConfig.appInstallationParams,
                  ).forEach(([key, value]) => {
                    const inputSchema = selectedApp.requiredInputs?.find(
                      (input) => input.name === key,
                    );
                    if (
                      inputSchema &&
                      inputSchema.type !== "password" &&
                      enrichedCurrentConfig?.visibleParams
                    ) {
                      enrichedCurrentConfig.visibleParams[key] = value;
                    }
                  });
                }
              }
            }

            return {
              ...s,
              hasFeedback: !!(
                s.feedbackHistory && s.feedbackHistory.length > 0
              ),
              config: s.config
                ? {
                    ...s.config,
                    availableApps: filteredApps,
                    knowledgeSchema: s.config.knowledgeSchema
                      ? JSON.parse(JSON.stringify(s.config.knowledgeSchema))
                      : undefined,
                  }
                : undefined,
              currentConfig: enrichedCurrentConfig,
            };
          }),
        })),
      })),
    ),
  );
};

// Helper function to transform API response to match frontend enums
const transformApiResponse = (data: any[]): WorkflowCategoryData[] => {
  if (!Array.isArray(data)) return [];

  return data.map((category) => ({
    ...category,
    // Transform office type from API format to enum format
    type:
      category.type === "front_office"
        ? OfficeType.FRONT_OFFICE
        : category.type === "back_office"
          ? OfficeType.BACK_OFFICE
          : category.type,
    processes:
      category.processes?.map((process: any) => ({
        ...process,
        steps:
          process.steps?.map((step: any) => ({
            ...step,
            // Transform status from API format to enum format
            status:
              step.status === "Incomplete"
                ? StepStatus.INCOMPLETE
                : step.status === "Complete"
                  ? StepStatus.COMPLETE
                  : step.status,
            // Transform step type from API format to enum format
            type:
              step.type === "app"
                ? StepType.APP
                : step.type === "knowledge"
                  ? StepType.KNOWLEDGE
                  : step.type === "hybrid"
                    ? StepType.HYBRID
                    : step.type,
          })) || [],
      })) || [],
  }));
};

export const fetchWorkflowData = async (): Promise<WorkflowCategoryData[]> => {
  logger.debug("API: Fetching workflow data...");

  try {
    const config = buildAxiosConfig();

    const { data } = await api.get(`${getBaseUrl()}/api/workflows`, config);

    const transformedData = transformApiResponse(data);
    return transformedData;
  } catch (error) {
    logger.error("Error fetching workflow data:", error);
    return getRefreshedCategories();
  }
};

export const submitStepConfiguration = async (
  payload: SubmitStepPayload,
): Promise<WorkflowCategoryData[]> => {
  logger.debug("API: Submitting step configuration...", payload);

  try {
    // Create a copy of the payload to avoid mutating the original
    const processedPayload = { ...payload };

    // Process any files in knowledge data
    if (processedPayload.config && processedPayload.config.knowledgeData) {
      logger.debug("Processing files in knowledge data...");

      // Check if knowledge data contains files that need to be uploaded
      if (Array.isArray(processedPayload.config.knowledgeData)) {
        // Process each knowledge item
        processedPayload.config.knowledgeData = await Promise.all(
          processedPayload.config.knowledgeData.map(async (item) => {
            const processedItem = { ...item };

            // Check each field for File objects
            for (const [fieldName, value] of Object.entries(item)) {
              if (Array.isArray(value)) {
                const files = value.filter((v) => v instanceof File) as File[];
                const existingFileDetails = value.filter(
                  (v) => !(v instanceof File),
                );

                if (files.length > 0) {
                  // Upload files and create attachment structure
                  const uploadedFiles = await Promise.all(
                    files.map(async (file: File) => {
                      const uploadResult = await uploadFile(file);
                      return {
                        id: uploadResult.fileName,
                        type: uploadResult.type,
                        size: uploadResult.size,
                        url: uploadResult.url,
                      };
                    }),
                  );

                  // Create the structure expected by backend: array with attachments object
                  processedItem[fieldName] = [
                    ...existingFileDetails, // Keep existing FileDetails if any
                    {
                      attachments: uploadedFiles,
                    },
                  ];
                } else {
                  // No files to upload, keep existing FileDetails
                  processedItem[fieldName] = existingFileDetails;
                }
              }
            }

            return processedItem;
          }),
        );
      }
    }

    const config = buildAxiosConfig({ "Content-Type": "application/json" });

    const { data } = await api.post(
      `${getBaseUrl()}/api/workflow/step/config`,
      processedPayload,
      config,
    );
    return data;
  } catch (error) {
    logger.error("Error submitting step configuration:", error);
    // Fallback to mock implementation if API fails
    return new Promise((resolve, reject) => {
      const { categoryId, processId, stepId, config } = payload;

      if (
        config.selectedAppId &&
        config.appInstallationParams &&
        Object.keys(config.appInstallationParams).length > 0
      ) {
        const appToInstall = availableAppsList.find(
          (app) => app.id === config.selectedAppId,
        );
        if (appToInstall && !appToInstall.isInstalled) {
          logger.debug(
            `API: Installing app ${config.selectedAppId} with params:`,
            config.appInstallationParams,
          );
          const globalAppIndex = availableAppsList.findIndex(
            (app) => app.id === config.selectedAppId,
          );
          if (globalAppIndex !== -1) {
            availableAppsList[globalAppIndex].isInstalled = true;
            availableAppsList[globalAppIndex].installationParams = {
              ...config.appInstallationParams,
            };
          }
        }
      }

      const categoryIndex = mockCategories.findIndex(
        (cat) => cat.id === categoryId,
      );
      if (categoryIndex === -1) return reject(new Error("Category not found"));

      const processIndex = mockCategories[categoryIndex].processes.findIndex(
        (proc) => proc.id === processId,
      );
      if (processIndex === -1) return reject(new Error("Process not found"));

      const stepIndex = mockCategories[categoryIndex].processes[
        processIndex
      ].steps.findIndex((step) => step.id === stepId);
      if (stepIndex === -1) return reject(new Error("Step not found"));

      const { appInstallationParams, ...restOfConfig } = config;
      mockCategories[categoryIndex].processes[processIndex].steps[
        stepIndex
      ].currentConfig = restOfConfig;
      mockCategories[categoryIndex].processes[processIndex].steps[
        stepIndex
      ].status = StepStatus.COMPLETE;
      mockCategories[categoryIndex].processes[processIndex].steps[
        stepIndex
      ].requiresConfiguration = false;

      resolve(getRefreshedCategories());
    });
  }
};

// File upload response interface
interface FileUploadResponse {
  message: string;
  url: string;
  fileName: string;
  size: number;
  type: string;
}

// Upload a single file to the server
export const uploadFile = async (
  file: File,
  folder: string = "",
): Promise<FileUploadResponse> => {
  const formData = new FormData();
  formData.append("file", file);
  if (folder) formData.append("folder", folder);

  try {
    // Build config but override the Content-Type to undefined
    // This allows Axios/browser to auto-set multipart/form-data with boundary
    const config = {
      ...buildAxiosConfig(),
      headers: {
        ...buildApiHeaders(), // Get auth headers without Content-Type
        // Explicitly setting to undefined removes the default application/json
        "Content-Type": "multipart/form-data",
      },
    };

    const { data } = await api.post(
      `${getBaseUrl()}/api/workflow/upload`,
      formData,
      config,
    );

    return data;
  } catch (error) {
    logger.error("Error uploading file:", error);
    throw error;
  }
};

export interface TemplateMediaUploadResponse {
  url: string;
  fileName: string;
  size: number;
  type: string;
}

export const uploadTemplateMedia = async (
  file: File,
): Promise<TemplateMediaUploadResponse> => {
  const formData = new FormData();
  formData.append("file", file);

  const config = {
    ...buildAxiosConfig(),
    headers: {
      ...buildApiHeaders(),
      "Content-Type": "multipart/form-data",
    },
  };

  const { data } = await api.post(
    `${getBaseUrl()}/api/template-media/upload`,
    formData,
    config,
  );

  return data;
};

export const submitFeedback = async (
  payload: SubmitFeedbackPayload,
): Promise<{
  success: boolean;
  message: string;
  updatedCategories: WorkflowCategoryData[];
}> => {
  logger.debug("API: Submitting feedback...", payload);

  try {
    let uploadedFiles: FileUploadResponse[] = [];

    // First, upload all files if there are any
    if (payload.newAttachments && payload.newAttachments.length > 0) {
      logger.debug("Uploading files first...");
      const uploadPromises = payload.newAttachments.map((file) =>
        uploadFile(file),
      );
      uploadedFiles = await Promise.all(uploadPromises);
      logger.debug("Files uploaded successfully:", uploadedFiles);
    }

    // Prepare the feedback payload with uploaded file URLs
    const feedbackData = {
      targetType: payload.targetType,
      targetId: payload.targetId,
      feedbackText: payload.feedbackText,
      attachments: uploadedFiles.map((file) => ({
        id: file.fileName, // Use fileName as ID or generate UUID
        name: file.fileName,
        size: file.size,
        type: file.type,
        url: file.url,
      })),
    };

    const { data } = await api.post(
      `${getBaseUrl()}/api/workflow/feedback`,
      feedbackData,
      buildAxiosConfig({ "Content-Type": "application/json" }),
    );
    return data;
  } catch (error) {
    logger.error("Error submitting feedback:", error);
    // Fallback to mock implementation if API fails
    const newFeedbackEntry: FeedbackEntry = {
      id: uuidv4(),
      text: payload.feedbackText,
      date: new Date().toISOString(),
      author: "Current User",
    };

    if (payload.newAttachments && payload.newAttachments.length > 0) {
      newFeedbackEntry.attachments = payload.newAttachments.map((file) => ({
        id: uuidv4(),
        name: file.name,
        size: file.size,
        type: file.type,
        url: `mock-url/${uuidv4()}-${file.name}`,
      }));
    }

    let itemUpdated = false;
    mockCategories = mockCategories.map((cat) => {
      if (payload.targetType === "category" && cat.id === payload.targetId) {
        cat.feedbackHistory = [
          ...(cat.feedbackHistory || []),
          newFeedbackEntry,
        ];
        cat.hasFeedback = true;
        itemUpdated = true;
        return cat;
      }
      return {
        ...cat,
        processes: cat.processes.map((proc) => ({
          ...proc,
          steps: proc.steps.map((step) => {
            if (payload.targetType === "step" && step.id === payload.targetId) {
              step.feedbackHistory = [
                ...(step.feedbackHistory || []),
                newFeedbackEntry,
              ];
              step.hasFeedback = true;
              itemUpdated = true;
            }
            return step;
          }),
        })),
      };
    });

    if (itemUpdated) {
      return {
        success: true,
        message: "Feedback submitted successfully!",
        updatedCategories: getRefreshedCategories(),
      };
    } else {
      return {
        success: false,
        message: "Target for feedback not found.",
        updatedCategories: getRefreshedCategories(),
      };
    }
  }
};

export const deleteWorkflowStep = async (
  payload: DeleteStepPayload,
): Promise<WorkflowCategoryData[]> => {
  logger.debug("API: Deleting step...", payload);

  try {
    // Prepare the delete request body
    const deleteRequest = {
      categoryId: payload.categoryId,
      processId: payload.processId,
      stepId: payload.stepId,
    };

    const { data } = await api.delete(`${getBaseUrl()}/api/workflow/step`, {
      data: deleteRequest, // DELETE request with body
      ...buildAxiosConfig({ "Content-Type": "application/json" }),
    });

    return data;
  } catch (error) {
    logger.error("Error deleting workflow step:", error);
    // Fallback to mock implementation if API fails
    return new Promise((resolve, reject) => {
      const { categoryId, processId, stepId } = payload;
      const categoryIndex = mockCategories.findIndex(
        (cat) => cat.id === categoryId,
      );
      if (categoryIndex === -1)
        return reject(new Error("Category not found for deletion"));

      const processIndex = mockCategories[categoryIndex].processes.findIndex(
        (proc) => proc.id === processId,
      );
      if (processIndex === -1)
        return reject(new Error("Process not found for deletion"));

      const stepIndex = mockCategories[categoryIndex].processes[
        processIndex
      ].steps.findIndex((step) => step.id === stepId);
      if (stepIndex === -1)
        return reject(new Error("Step not found for deletion"));

      mockCategories[categoryIndex].processes[processIndex].steps.splice(
        stepIndex,
        1,
      );

      resolve(getRefreshedCategories());
    });
  }
};

// Re-export download functionality
export { downloadFile, isAgentProxyUrl } from "./downloadFile";
